import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { isNil } from "lodash-es";

import { PdfState } from "../../shared/types/worksheet";
import { type BlockType } from "../../shared/types/worksheet";
import { logInfoWithDebugFlag } from "../../utils/logger";

import { STUDENT_VIEW_QUERY_PARAM, TIMELINE_VIEW_QUERY_PARAM } from "components/constants";
import { ViewportDimensions } from "components/materials/presentation/projector/projector-messaging";
import type { PageTabVariant } from "providers/DocViewProvider";
import { createReplaceAllReducer } from "store/utils";
import { getDefaultPdfState } from "utils/asset";

// TODO: Move all query param handling into the store
const initialQueryParams = new URLSearchParams(window.location.search);

// This is just for logging purposes, not to be used for a page refresh. This is used
// to track erroneous data that may be loaded into the UX when it shouldn't have been.
// It's only used by lowerCourseVersionTo at the moment, which is only called in
// pubsub-slice on API response data--if the course version returned differs by more
// than 50, there is a good chance that the API call was not filtered by the course.
const MAX_COURSE_VERSION_DIFF_FOR_LOGGING = 50;

const boolFromParam = (paramName: string) => {
  const value = initialQueryParams.get(paramName);
  return value === "1";
};

const replaceBoolQueryParam = (paramName: string, value: boolean) => {
  // This function specifically does not trigger a reload or history change
  const newParams = new URLSearchParams(window.location.search);
  if (value) {
    newParams.set(paramName, "1");
  } else {
    newParams.delete(paramName);
  }
  window.history.replaceState({}, "", `${window.location.pathname}?${newParams.toString()}`);
};

type ViewState = {
  isCourseVersionOutOfSync: boolean;
  courseVersion: number;
  isStudentPreview: boolean;
  isTimeline: boolean;
  shouldShowAssessmentSummary: boolean;
  shouldShowModalOnExitAssessmentMode: boolean;
  shouldResetPageTab: boolean;
  isGroupConfigShown: boolean;
  selectedPageTabVariant: PageTabVariant | null;
  assetModalIsOpen: boolean;
  preventFeatureModalClosing: boolean;
  featuringPdfState: PdfState;
  currentCourseId: string | null;
  viewingScreenshare: boolean;
  // We have logic based on whether current course is set or not so we need a separate field for selecting a course
  // in the admin view.
  adminCourseId: string | null;
  courseSettingsTabIndex: number | undefined;

  // For Page Block Table of Contents
  selectedPageBlockIndex: number | null;
  currentAvailablePageBlocks: { index: number; blockType: BlockType["t"] }[];

  // Websocket/Save status indicators
  lastOpAt: number | null;
  lastOpSavedAt: number | null;
  opError: string | null;
  connectionQuality: "poor" | "good" | "fair";
  userIsInactive: boolean;
  // Query params as a string, without the leading ?
  queryParams: string;
  /* projector dimensions */
  projectorHeight: number | null;
  projectorFeaturedAssetContainer:
    | (ViewportDimensions & {
        hasHorizontalScrollbar: boolean;
        hasVerticalScrollbar: boolean;
      })
    | null;
  /* stored collaboration server version for force reload mechanism */
  storedCollaborationServerVersion: string | null;
  /* flag to show force reload modal */
  showForceReloadModal: boolean;
};

const initialState = () =>
  ({
    courseVersion: 0,
    currentCourseId: null,
    isCourseVersionOutOfSync: false,
    isStudentPreview: boolFromParam(STUDENT_VIEW_QUERY_PARAM),
    isTimeline: boolFromParam(TIMELINE_VIEW_QUERY_PARAM),
    isGroupConfigShown: false,
    selectedPageTabVariant: "my_draft",
    assetModalIsOpen: false,
    preventFeatureModalClosing: false,
    featuringPdfState: getDefaultPdfState(),
    currentAvailablePageBlocks: [],
    selectedPageBlockIndex: null,
    adminCourseId: null,
    shouldShowAssessmentSummary: false,
    shouldShowModalOnExitAssessmentMode: false,
    shouldResetPageTab: false,
    viewingScreenshare: false,
    courseSettingsTabIndex: undefined,
    lastOpAt: null,
    lastOpSavedAt: null,
    opError: null,
    connectionQuality: "good",
    userIsInactive: false,
    queryParams: new URLSearchParams(window.location.search).toString(),
    projectorHeight: null,
    projectorFeaturedAssetContainer: null,
    storedCollaborationServerVersion: null,
    showForceReloadModal: false,
  }) as ViewState;

export const ViewStore = createSlice({
  name: "view",
  initialState,
  reducers: {
    /**
     * If we are updating the course version number from the API (pubsub-slice), we want the
     * course version to be set to the lowest course version received. This is because we don't
     * know what messages we may miss in between loading the data on the page and pubsub messages
     * in between, so we pull the few messages we may miss in this transition period. Nothing is
     * set if the new course version is undefined. */
    lowerCourseVersionTo: (
      state,
      action: PayloadAction<{
        results: { course_version?: number; course_id?: string }[] | undefined;
        modelName: string;
        params?: Record<string, string>;
      }>,
    ) => {
      const { results, modelName, params } = action.payload;
      const courseId = state.currentCourseId;

      const result = results ? results.find((res) => res.course_id === courseId) : undefined;
      if (!result) {
        if (courseId) {
          logInfoWithDebugFlag(
            "debug_pubsub",
            "Why are we returning data for a different course?",
            {
              result,
              currentCourseId: courseId,
              modelName,
              params,
            },
          );
        }

        return;
      }

      const newCourseVersion = result?.course_version;
      const oldCourseVersion = state.courseVersion;
      if (!isNil(newCourseVersion) && newCourseVersion < oldCourseVersion) {
        const versionDiff = Math.abs(newCourseVersion - oldCourseVersion);
        if (versionDiff >= MAX_COURSE_VERSION_DIFF_FOR_LOGGING) {
          logInfoWithDebugFlag(
            "debug_pubsub",
            `Lowering course version, version diff: ${versionDiff}`,
            {
              modelName,
              currentCourseId: courseId,
              newCourseVersion,
              oldCourseVersion,
            },
          );
        }

        state.courseVersion = newCourseVersion;
      }
    },
    /** Whenever we switch courses, we set the course version to the course's course_version.
     * This may do nothing because we already load the course version when we load data from
     * the API. Nothing is set if the new course version is undefined. */
    setCourseVersionTo: (state, action: PayloadAction<number | undefined>) => {
      const newCourseVersion = action.payload;
      if (!isNil(newCourseVersion)) {
        state.courseVersion = newCourseVersion;
      }
    },
    setIsCourseVersionOutOfSync: (state, action: PayloadAction<boolean>) => {
      state.isCourseVersionOutOfSync = action.payload;
    },
    setShowAssessmentSummary: (state, action: PayloadAction<boolean>) => {
      state.shouldShowAssessmentSummary = action.payload;
    },
    setShouldShowModalOnExitAssessmentMode: (state, action: PayloadAction<boolean>) => {
      state.shouldShowModalOnExitAssessmentMode = action.payload;
    },
    setShouldResetPageTab: (state, action: PayloadAction<boolean>) => {
      state.shouldResetPageTab = action.payload;
    },
    setAssetModalIsOpen: (state, action: PayloadAction<boolean>) => {
      state.assetModalIsOpen = action.payload;
    },
    setPreventFeatureModalClosing: (state, action: PayloadAction<boolean>) => {
      state.preventFeatureModalClosing = action.payload;
    },
    clearFeaturingPdfState: (state) => {
      state.featuringPdfState = getDefaultPdfState();
    },
    setFeaturingPdfState: (state, action: PayloadAction<Partial<PdfState>>) => {
      state.featuringPdfState = { ...state.featuringPdfState, ...action.payload };
    },
    setSelectedPageTabVariant: (state, action: PayloadAction<PageTabVariant | null>) => {
      state.selectedPageTabVariant = action.payload;
    },
    setIsStudentPreview: (state, action: PayloadAction<boolean>) => {
      state.isStudentPreview = action.payload;
      replaceBoolQueryParam(STUDENT_VIEW_QUERY_PARAM, action.payload);
    },
    setIsTimelineView: (state, action: PayloadAction<boolean>) => {
      state.isTimeline = action.payload;
      replaceBoolQueryParam(TIMELINE_VIEW_QUERY_PARAM, action.payload);
    },
    onSendSharedbOp: (state) => {
      state.lastOpAt = new Date().getTime();
    },
    onReceiveSharedbOp: (state) => {
      state.lastOpSavedAt = new Date().getTime();
      state.opError = null;
    },
    onSharedbError: (state, action: PayloadAction<string>) => {
      state.opError = action.payload;
    },
    setCurrentCourseId: (state, action: PayloadAction<string | null>) => {
      state.currentCourseId = action.payload;
    },
    setShowGroupConfig: (state, action: PayloadAction<boolean>) => {
      state.isGroupConfigShown = action.payload;
    },
    // Don't use this directly, use the useQueryParamsManager hook instead.
    updateQueryParams: (state, action: PayloadAction<string>) => {
      state.queryParams = action.payload;
    },
    setViewingScreenshare: (state, action: PayloadAction<boolean>) => {
      state.viewingScreenshare = action.payload;
    },
    setSelectedPageBlockIndex: (state, action: PayloadAction<number | null>) => {
      state.selectedPageBlockIndex = action.payload;
    },
    setCurrentAvailablePageBlocks: (
      state,
      action: PayloadAction<{ index: number; blockType: BlockType["t"] }[]>,
    ) => {
      state.currentAvailablePageBlocks = action.payload;
    },
    setCourseSettingsForCourseId: (state, action: PayloadAction<string | null>) => {
      state.adminCourseId = action.payload;
      state.courseSettingsTabIndex = action.payload !== null ? 1 : undefined; // Default to Course Info tab
    },
    setCourseSettingsTabIndex: (state, action: PayloadAction<number | undefined>) => {
      state.courseSettingsTabIndex = action.payload;
      if (action.payload === undefined) {
        state.adminCourseId = null;
      }
    },
    setConnectionQuality: (state, action: PayloadAction<"poor" | "good" | "fair">) => {
      state.connectionQuality = action.payload;
    },
    setUserIsInactive: (state, action: PayloadAction<boolean>) => {
      state.userIsInactive = action.payload;
    },
    setProjectorHeight: (state, action: PayloadAction<number | null>) => {
      state.projectorHeight = action.payload;
    },
    setProjectorFeaturedAssetContainer: (
      state,
      action: PayloadAction<
        | (ViewportDimensions & {
            hasHorizontalScrollbar: boolean;
            hasVerticalScrollbar: boolean;
          })
        | null
      >,
    ) => {
      state.projectorFeaturedAssetContainer = action.payload;
    },
    setStoredCollaborationServerVersion: (state, action: PayloadAction<string | null>) => {
      state.storedCollaborationServerVersion = action.payload;
    },
    setShowForceReloadModal: (state, action: PayloadAction<boolean>) => {
      state.showForceReloadModal = action.payload;
    },
  },
  extraReducers: createReplaceAllReducer<ViewState>("view", initialState),
});

export const viewStateActions = ViewStore.actions;
